import { Add } from "@mui/icons-material";
import { Fab, Stack } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { parseAsString } from "nuqs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import GaiaDataList from "~/modules/app/components/GaiaDataList";
import GaiaPageGridComponent from "~/modules/app/components/GaiaPageGridComponent";
import GaiaPageLayoutColumn from "~/modules/app/components/GaiaPageLayoutColumn";
import GaiaTableLinkCell from "~/modules/app/components/GaiaTableLinkCell";
import ErrorScreen from "~/modules/app/screens/ErrorScreen";
import { renderItalicCell } from "~/modules/settings/util/dataGridHelper";
import SearchInput from "~/shared/components/SearchInput";
import { useDebouncedValue } from "~/shared/hooks/useDebouncedValue";
import { useGridQueryState } from "~/shared/hooks/useGridQueryState";

import useSettingsResearchTypeListRoute_GetAll from "./api/useSettingsResearchTypeListRoute_GetAll";
import { ResearchTypeListFragment } from "./fragments.generated";

export default function SettingsResearchTypeListRoute() {
    const { t } = useTranslation("researchType");

    const [queryState, setQueryState, gridModelProps] = useGridQueryState({
        q: parseAsString.withDefault(""),
    });

    const debouncedQ = useDebouncedValue(queryState.q);

    const params = useMemo(
        () => ({
            q: debouncedQ || undefined,
            page: queryState.page,
            pageSize: queryState.pageSize,
            sortBy: queryState.sortBy,
            sortOrder: queryState.sortOrder,
        }),
        [debouncedQ, queryState],
    );

    const { researchTypes, loading, error, total } = useSettingsResearchTypeListRoute_GetAll(params);

    const handleParameterChange = <T,>(key: string, value: T) => {
        setQueryState({ [key]: value, page: 0 } as Parameters<typeof setQueryState>[0]);
    };

    const columns: Array<GridColDef<ResearchTypeListFragment>> = useMemo(
        () => [
            {
                field: "name",
                headerName: t("settingsResearchTypeListRoute.headerName"),
                flex: 1,
            },
            {
                field: "speciesV2",
                headerName: t("settingsResearchTypeListRoute.headerSpecies"),
                flex: 1,
                valueGetter: (_, row) => row.speciesV2.map(x => x.species.name).join(", "),
                renderCell: renderItalicCell,
            },

            {
                field: "description",
                headerName: t("settingsResearchTypeListRoute.headerDescription"),
                flex: 1,
            },
            {
                type: "actions",
                field: "id",
                headerName: "",
                maxWidth: 40,
                renderCell: props => <GaiaTableLinkCell value={props.value} />,
            },
        ],
        [t],
    );

    if (error) {
        return <ErrorScreen error={error} />;
    }

    return (
        <GaiaPageLayoutColumn maxWidth="xl">
            <GaiaPageGridComponent
                title={t("settingsResearchTypeListRoute.title")}
                actions={
                    <Fab component={Link} to="new" color="primary">
                        <Add />
                    </Fab>
                }
            >
                <Stack direction="row" gap={1} flexWrap="wrap" useFlexGap sx={{ flex: 1 }}>
                    <SearchInput
                        label={t("settingsResearchTypeListRoute.filterSearch")}
                        value={queryState.q}
                        onChange={value => handleParameterChange("q", value)}
                    />
                </Stack>

                <GaiaDataList
                    data={researchTypes ?? []}
                    loading={loading}
                    error={error}
                    totalRows={total ?? 0}
                    columns={columns}
                    gridModels={gridModelProps}
                />
            </GaiaPageGridComponent>
        </GaiaPageLayoutColumn>
    );
}
